import {
  PLATFORM_TRAINING_START_EVENT,
  PLATFORM_TRAINING_STEP_EVENT,
  PLATFORM_TRAINING_STEPS,
  finishPlatformTraining,
  getPlatformTrainingState,
  setPlatformTrainingStep,
} from '@/helpers/platformTraining'
import { CloseOutlined } from '@ant-design/icons'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import s from './PlatformTrainingOverlay.module.css'

const ITEM_SELECTOR = '[data-platform-training-target="students-and-colleagues"]'
const FOLDER_SELECTOR = '[data-platform-training-folder-target="students-and-colleagues"]'
const OPEN_DROPDOWN_SELECTOR = '.ant-select-dropdown:not(.ant-select-dropdown-hidden)'
const SCROLL_LOCK_ATTRIBUTE = 'data-platform-training-scroll-locked'
const SCROLL_POSITION_ATTRIBUTE = 'data-platform-training-scroll-position'
const SCROLL_RESERVE_ATTRIBUTE = 'data-platform-training-scroll-reserve'
const SPOTLIGHT_PADDING = 6
const TOOLTIP_WIDTH = 360
const DEFAULT_TOOLTIP_HEIGHT = 360
const TARGET_SEARCH_TIMEOUT = 8000
const TARGET_PATHS = ['/users/students', '/users/colleges', '/manage']

const STEP_TARGETS = {
  [PLATFORM_TRAINING_STEPS.ADD_STUDENT]: 'add-student-button',
  [PLATFORM_TRAINING_STEPS.STUDENT_IDENTITY]: 'student-identity',
  [PLATFORM_TRAINING_STEPS.STUDENT_COURSE]: 'student-course',
  [PLATFORM_TRAINING_STEPS.STUDENT_TARIFF]: 'student-tariff',
  [PLATFORM_TRAINING_STEPS.STUDENT_TEACHER]: 'student-teacher',
  [PLATFORM_TRAINING_STEPS.STUDENT_DURATION]: 'student-duration',
  [PLATFORM_TRAINING_STEPS.STUDENT_LESSON_LINK]: 'student-lesson-link',
  [PLATFORM_TRAINING_STEPS.STUDENT_COST]: 'student-cost',
  [PLATFORM_TRAINING_STEPS.STUDENT_PAYMENT]: 'student-payment',
  [PLATFORM_TRAINING_STEPS.STUDENT_PAYMENT_METHOD]: 'student-payment-method',
  [PLATFORM_TRAINING_STEPS.STUDENT_SCHEDULE]: 'student-schedule',
  [PLATFORM_TRAINING_STEPS.STUDENT_PASSWORD]: 'student-password',
  [PLATFORM_TRAINING_STEPS.STUDENT_SAVE]: 'student-save',
}

const STEP_DESCRIPTIONS = {
  [PLATFORM_TRAINING_STEPS.ADD_STUDENT]:
    'Нажмите на данную кнопку и выберите «Точечно», чтобы добавить первого ученика.',
  [PLATFORM_TRAINING_STEPS.STUDENT_IDENTITY]:
    'Заполните ФИО ученика, e-mail, номер телефона или логин (можно заполнить несколько полей). Важно! Если вы регистрируете ученика по номеру телефона или логину, то ниже вам обязательно надо заполнить поле с паролем, чтобы учащийся смог зайти на платформу.',
  [PLATFORM_TRAINING_STEPS.STUDENT_COURSE]:
    'Выберите курс, на который вы хотите записать ученика, чтобы вы смогли назначить в расписании встречу и следить за оплатами. Если нужного курса для вас нет, то выберите Шаблонный курс, в последующем вы сможете изменить название и настройки курса, о чем мы поговорим позже.',
  [PLATFORM_TRAINING_STEPS.STUDENT_TARIFF]:
    'По умолчанию выбран индивидуальный тариф. На платформе также доступны групповые тарифы — подробнее о них расскажем далее.',
  [PLATFORM_TRAINING_STEPS.STUDENT_TEACHER]:
    'По умолчанию в этом поле указаны ваши ФИО. При необходимости вы сможете выбрать другого педагога.',
  [PLATFORM_TRAINING_STEPS.STUDENT_DURATION]:
    'Введите стандартную длительность занятия с этим учащимся. Относительно этого времени будет рассчитываться стоимость урока. (Например 1500 за 60 мин.), при этом сами занятия могут быть любой длительности.',
  [PLATFORM_TRAINING_STEPS.STUDENT_LESSON_LINK]:
    'По умолчанию проставлена постоянная ссылка на Импульс ВКС, но вы можете заменить её на другую.',
  [PLATFORM_TRAINING_STEPS.STUDENT_COST]:
    'Введите стоимость занятия стандартной длительности (длительность вы вписывали двумя шагами ранее).',
  [PLATFORM_TRAINING_STEPS.STUDENT_PAYMENT]:
    'Укажите текущий баланс учащегося, если он уже оплатил одно или несколько занятий. Если оплаты ещё не было, оставьте поле пустым — баланс можно будет изменить позднее. Подробнее об этом расскажем далее.',
  [PLATFORM_TRAINING_STEPS.STUDENT_PAYMENT_METHOD]:
    'Укажите способ, которым учащийся оплатил занятия. Эти данные необходимы для корректного формирования отчётности по платежам.',
  [PLATFORM_TRAINING_STEPS.STUDENT_SCHEDULE]:
    'Вы можете сразу настроить расписание учащегося. Однако на этом этапе пропустим данную функцию — добавление уроков в расписание рассмотрим отдельно в следующем разделе.',
  [PLATFORM_TRAINING_STEPS.STUDENT_PASSWORD]:
    'Поскольку вы регистрируете ученика по номеру телефона или логину, обязательно создайте для него пароль и передайте данные для входа.',
  [PLATFORM_TRAINING_STEPS.STUDENT_SAVE]:
    'Нажмите «Сохранить», чтобы завершить добавление ученика на платформу.',
  [PLATFORM_TRAINING_STEPS.CREATED_STUDENT]:
    'Поздравляем! Первый ученик успешно добавлен на платформу. Теперь перейдём к созданию уроков.',
}

const NEXT_STEPS = {
  [PLATFORM_TRAINING_STEPS.STUDENT_TARIFF]: PLATFORM_TRAINING_STEPS.STUDENT_TEACHER,
  [PLATFORM_TRAINING_STEPS.STUDENT_TEACHER]: PLATFORM_TRAINING_STEPS.STUDENT_DURATION,
  [PLATFORM_TRAINING_STEPS.STUDENT_DURATION]: PLATFORM_TRAINING_STEPS.STUDENT_LESSON_LINK,
  [PLATFORM_TRAINING_STEPS.STUDENT_LESSON_LINK]: PLATFORM_TRAINING_STEPS.STUDENT_COST,
  [PLATFORM_TRAINING_STEPS.STUDENT_COST]: PLATFORM_TRAINING_STEPS.STUDENT_PAYMENT,
}

const PREVIOUS_STEPS = {
  [PLATFORM_TRAINING_STEPS.STUDENT_COURSE]: PLATFORM_TRAINING_STEPS.STUDENT_IDENTITY,
  [PLATFORM_TRAINING_STEPS.STUDENT_TARIFF]: PLATFORM_TRAINING_STEPS.STUDENT_COURSE,
  [PLATFORM_TRAINING_STEPS.STUDENT_TEACHER]: PLATFORM_TRAINING_STEPS.STUDENT_TARIFF,
  [PLATFORM_TRAINING_STEPS.STUDENT_DURATION]: PLATFORM_TRAINING_STEPS.STUDENT_TEACHER,
  [PLATFORM_TRAINING_STEPS.STUDENT_LESSON_LINK]: PLATFORM_TRAINING_STEPS.STUDENT_DURATION,
  [PLATFORM_TRAINING_STEPS.STUDENT_COST]: PLATFORM_TRAINING_STEPS.STUDENT_LESSON_LINK,
  [PLATFORM_TRAINING_STEPS.STUDENT_PAYMENT]: PLATFORM_TRAINING_STEPS.STUDENT_COST,
  [PLATFORM_TRAINING_STEPS.STUDENT_PAYMENT_METHOD]: PLATFORM_TRAINING_STEPS.STUDENT_PAYMENT,
  [PLATFORM_TRAINING_STEPS.STUDENT_PASSWORD]: PLATFORM_TRAINING_STEPS.STUDENT_SCHEDULE,
}

const getIdentityErrorDescription = (fields = []) => {
  if (fields.length === 1) {
    const descriptions = {
      email: 'Такая почта уже используется.',
      phone: 'Такой номер телефона уже используется.',
      login: 'Такой логин уже используется.',
    }

    return `${descriptions[fields[0]]} Измените данные учащегося и нажмите «Далее».`
  }

  return 'Некоторые из указанных данных уже используются: почта, номер телефона или логин. Измените их и нажмите «Далее».'
}

const findTargetItem = () => {
  const markedItem = document.querySelector(ITEM_SELECTOR)
  if (markedItem) return markedItem

  return [...document.querySelectorAll('.app-sidebar a, nav a')].find((element) => {
    const path = element.getAttribute('href')?.split('?')?.[0]
    const title = element.textContent?.toLocaleLowerCase?.('ru-RU') ?? ''

    return TARGET_PATHS.includes(path)
      || (title.includes('ученик') && title.includes('коллег'))
  }) ?? null
}

const getVisibleElements = (selector) => (
  [...document.querySelectorAll(selector)].filter((element) => {
    const rect = element.getBoundingClientRect()
    return rect.width > 0 && rect.height > 0
  })
)

const getOpenDropdowns = () => getVisibleElements(OPEN_DROPDOWN_SELECTOR)

const waitForStepTarget = (currentStep, nextStep, targetName) => {
  const startedAt = Date.now()

  const tryAdvance = () => {
    const state = getPlatformTrainingState()
    if (!state?.active || state.step !== currentStep) return

    const targetIsReady = getVisibleElements(
      `[data-platform-training-target="${targetName}"]`,
    ).length > 0

    if (targetIsReady && getOpenDropdowns().length === 0) {
      setPlatformTrainingStep(nextStep)
      return
    }

    if (Date.now() - startedAt < TARGET_SEARCH_TIMEOUT) {
      window.setTimeout(tryAdvance, 80)
    }
  }

  window.setTimeout(tryAdvance, 0)
}

const getElementClippingRect = (element) => {
  const clippingRect = {
    top: 8,
    right: window.innerWidth - 8,
    bottom: window.innerHeight - 8,
    left: 8,
  }
  let parent = element?.parentElement

  while (parent && parent !== document.body) {
    const style = window.getComputedStyle(parent)
    const clipsContent = [style.overflow, style.overflowX, style.overflowY]
      .some((value) => /(auto|scroll|hidden|clip)/.test(value))

    if (clipsContent) {
      const rect = parent.getBoundingClientRect()
      clippingRect.top = Math.max(clippingRect.top, rect.top)
      clippingRect.right = Math.min(clippingRect.right, rect.right)
      clippingRect.bottom = Math.min(clippingRect.bottom, rect.bottom)
      clippingRect.left = Math.max(clippingRect.left, rect.left)
    }

    parent = parent.parentElement
  }

  return clippingRect
}

const getElementVisibility = (element) => {
  const rect = element.getBoundingClientRect()
  const clippingRect = getElementClippingRect(element)
  const availableHeight = clippingRect.bottom - clippingRect.top
  const isTallerThanViewport = rect.height > availableHeight
  const isFullyVerticallyVisible = rect.top >= clippingRect.top
    && rect.bottom <= clippingRect.bottom
  const isOversizedElementVisible = isTallerThanViewport
    && rect.bottom > clippingRect.top
    && rect.top < clippingRect.bottom
  const isHorizontallyVisible = rect.left >= clippingRect.left
    && rect.right <= clippingRect.right

  return {
    isFullyVerticallyVisible,
    isHorizontallyVisible,
    isOversizedElementVisible,
  }
}

const getScrollableParent = (element) => {
  let parent = element?.parentElement

  while (parent && parent !== document.body) {
    const { overflowY } = window.getComputedStyle(parent)
    const canScroll = /(auto|scroll)/.test(overflowY)
      || parent.hasAttribute(SCROLL_LOCK_ATTRIBUTE)

    if (
      parent.classList.contains('modal__background')
      || (canScroll && parent.scrollHeight > parent.clientHeight)
    ) return parent
    parent = parent.parentElement
  }

  return null
}

const centerElementInScrollParent = (element, preparedScrollParent = null) => {
  const scrollParent = preparedScrollParent ?? getScrollableParent(element)
  if (!scrollParent) return false

  const targetRect = element.getBoundingClientRect()
  const scrollParentRect = scrollParent.getBoundingClientRect()
  const targetCenter = targetRect.top + targetRect.height / 2
  const scrollParentCenter = scrollParentRect.top + scrollParent.clientHeight / 2
  const maxScrollTop = Math.max(0, scrollParent.scrollHeight - scrollParent.clientHeight)
  const nextScrollTop = Math.max(
    0,
    Math.min(maxScrollTop, scrollParent.scrollTop + targetCenter - scrollParentCenter),
  )

  if (scrollParent.hasAttribute(SCROLL_LOCK_ATTRIBUTE)) {
    scrollParent.setAttribute(SCROLL_POSITION_ATTRIBUTE, String(nextScrollTop))
  }
  scrollParent.scrollTop = nextScrollTop
  return true
}

const ensureElementIsVisible = (element) => {
  centerElementInScrollParent(element)

  const nextVisibility = getElementVisibility(element)
  return (
    nextVisibility.isFullyVerticallyVisible
      || nextVisibility.isOversizedElementVisible
  ) && nextVisibility.isHorizontallyVisible
}

const getStepElements = (trainingState) => {
  const { step, studentId, studentName } = trainingState ?? {}

  if (step === PLATFORM_TRAINING_STEPS.SIDEBAR) {
    const item = findTargetItem()
    const folder = document.querySelector(FOLDER_SELECTOR)
    const isFolderOpen = folder?.getAttribute('data-platform-training-folder-open') === 'true'
    const element = folder && !isFolderOpen ? folder : item

    return element
      ? { elements: [element], phase: element === folder ? 'folder' : 'item' }
      : null
  }

  if (step === PLATFORM_TRAINING_STEPS.ADD_STUDENT) {
    const pointOption = getVisibleElements(
      '[data-platform-training-target="add-student-point"]',
    )
    const elements = pointOption.length
      ? pointOption
      : getVisibleElements('[data-platform-training-target="add-student-button"]')

    return elements.length
      ? { elements, phase: pointOption.length ? 'point-option' : step }
      : null
  }

  if (step === PLATFORM_TRAINING_STEPS.CREATED_STUDENT) {
    const studentElements = [...document.querySelectorAll('[data-platform-training-student-id]')]
    const element = studentElements.find(
      (item) => studentId &&
        String(item.getAttribute('data-platform-training-student-id')) === String(studentId),
    ) ?? studentElements.find(
      (item) => studentName && item.textContent?.includes(studentName),
    )

    return element ? { elements: [element], phase: step } : null
  }

  const target = STEP_TARGETS[step]
  if (!target) return null

  const elements = getVisibleElements(`[data-platform-training-target="${target}"]`)
  if (
    [
      PLATFORM_TRAINING_STEPS.STUDENT_COURSE,
      PLATFORM_TRAINING_STEPS.STUDENT_TARIFF,
      PLATFORM_TRAINING_STEPS.STUDENT_TEACHER,
      PLATFORM_TRAINING_STEPS.STUDENT_PAYMENT_METHOD,
    ].includes(step)
  ) {
    elements.push(
      ...getOpenDropdowns(),
    )
  }

  return elements.length ? { elements, phase: step } : null
}

const getSpotlightState = (
  trainingState,
  { target: preparedTarget = null, ensureVisible = true } = {},
) => {
  const target = preparedTarget ?? getStepElements(trainingState)
  if (!target) return null

  if (ensureVisible && !ensureElementIsVisible(target.elements[0])) return null

  const sourceRects = target.elements
    .map((element) => element.getBoundingClientRect())
    .filter((rect) => rect.width > 0 && rect.height > 0)

  if (!sourceRects.length) return null

  const sourceRect = {
    top: Math.min(...sourceRects.map((rect) => rect.top)),
    right: Math.max(...sourceRects.map((rect) => rect.right)),
    bottom: Math.max(...sourceRects.map((rect) => rect.bottom)),
    left: Math.min(...sourceRects.map((rect) => rect.left)),
  }

  return {
    ...target,
    element: target.elements[0],
    rect: {
      top: Math.max(0, sourceRect.top - SPOTLIGHT_PADDING),
      right: Math.min(window.innerWidth, sourceRect.right + SPOTLIGHT_PADDING),
      bottom: Math.min(window.innerHeight, sourceRect.bottom + SPOTLIGHT_PADDING),
      left: Math.max(0, sourceRect.left - SPOTLIGHT_PADDING),
    },
  }
}

const getTooltipPosition = (rect, tooltipSize) => {
  const tooltipWidth = tooltipSize?.width ?? TOOLTIP_WIDTH
  const availableTooltipHeight = Math.max(0, window.innerHeight - 32)
  const tooltipHeight = Math.min(
    tooltipSize?.height ?? DEFAULT_TOOLTIP_HEIGHT,
    availableTooltipHeight,
  )
  const hasSpaceRight = rect.right + tooltipWidth + 32 <= window.innerWidth
  const hasSpaceLeft = rect.left >= tooltipWidth + 32

  if (hasSpaceRight || hasSpaceLeft) {
    const top = Math.max(16, rect.top)
    const fitsFromTargetTop = top + tooltipHeight + 16 <= window.innerHeight

    return {
      left: hasSpaceRight ? rect.right + 16 : rect.left - tooltipWidth - 16,
      ...(fitsFromTargetTop ? { top } : { bottom: 16 }),
    }
  }

  const top = rect.bottom + tooltipHeight + 16 <= window.innerHeight
    ? rect.bottom + 12
    : Math.max(16, rect.top - tooltipHeight - 12)

  return {
    left: Math.max(16, Math.min(rect.left, window.innerWidth - tooltipWidth - 16)),
    top,
  }
}

const getTargetInputValue = (element, name) => (
  element?.querySelector(`input[name="${name}"]`)?.value?.trim?.() ?? ''
)

const hasSelectedOption = (element) => (
  !!element?.querySelector('.ant-select-selection-item')
)

const isSameSpotlight = (currentSpotlight, nextSpotlight) => {
  if (currentSpotlight === nextSpotlight) return true
  if (!currentSpotlight || !nextSpotlight) return false
  if (
    currentSpotlight.element !== nextSpotlight.element
    || currentSpotlight.phase !== nextSpotlight.phase
    || currentSpotlight.elements.length !== nextSpotlight.elements.length
  ) return false

  return ['top', 'right', 'bottom', 'left'].every(
    (side) => Math.abs(currentSpotlight.rect[side] - nextSpotlight.rect[side]) < 0.5,
  )
}

const PlatformTrainingOverlay = () => {
  const history = useHistory()
  const isTutorPlatform = useSelector(
    (store) => store.settings?.platform_type === 'tutor',
  )
  const [trainingState, setTrainingState] = useState(() => getPlatformTrainingState())
  const [spotlight, setSpotlight] = useState(null)
  const [isTargetMissing, setIsTargetMissing] = useState(false)
  const [searchAttempt, setSearchAttempt] = useState(0)
  const [tooltipSize, setTooltipSize] = useState(null)
  const [fieldValuesRevision, setFieldValuesRevision] = useState(0)
  const tooltipRef = useRef(null)
  const positionedTargetKeyRef = useRef(null)
  const isOpen = isTutorPlatform && trainingState?.active === true
  const spotlightElement = spotlight?.element

  const closeTraining = useCallback(() => {
    finishPlatformTraining()
    setTrainingState(null)
    setSpotlight(null)
    setIsTargetMissing(false)
  }, [])

  useEffect(() => {
    const handleStateChange = (event) => {
      setTooltipSize(null)
      setSpotlight(null)
      setIsTargetMissing(false)
      positionedTargetKeyRef.current = null
      setSearchAttempt((attempt) => attempt + 1)
      setTrainingState(event.detail ?? getPlatformTrainingState())
    }

    window.addEventListener(PLATFORM_TRAINING_START_EVENT, handleStateChange)
    window.addEventListener(PLATFORM_TRAINING_STEP_EVENT, handleStateChange)
    return () => {
      window.removeEventListener(PLATFORM_TRAINING_START_EVENT, handleStateChange)
      window.removeEventListener(PLATFORM_TRAINING_STEP_EVENT, handleStateChange)
    }
  }, [])

  useLayoutEffect(() => {
    if (!spotlight || !tooltipRef.current) return

    const rect = tooltipRef.current.getBoundingClientRect()
    const nextSize = {
      step: trainingState?.step,
      width: rect.width,
      height: rect.height,
    }
    setTooltipSize((currentSize) => (
      currentSize?.step === nextSize.step
        && currentSize?.width === nextSize.width
        && currentSize?.height === nextSize.height
        ? currentSize
        : nextSize
    ))
  }, [fieldValuesRevision, spotlight, trainingState?.step])

  useEffect(() => {
    if (!isOpen || !spotlightElement) return undefined

    const handleFieldValueChange = () => {
      setFieldValuesRevision((revision) => revision + 1)
    }

    spotlightElement.addEventListener('input', handleFieldValueChange)
    spotlightElement.addEventListener('change', handleFieldValueChange)

    return () => {
      spotlightElement.removeEventListener('input', handleFieldValueChange)
      spotlightElement.removeEventListener('change', handleFieldValueChange)
    }
  }, [isOpen, spotlightElement])

  useLayoutEffect(() => {
    if (!isOpen) return undefined

    const targetElement = getStepElements(trainingState)?.elements[0] ?? spotlightElement
    const scrollParent = getScrollableParent(targetElement)
    if (!scrollParent) return undefined

    const previousScrollTop = scrollParent.scrollTop
    const previousScrollLockAttribute = scrollParent.getAttribute(SCROLL_LOCK_ATTRIBUTE)
    const previousScrollPositionAttribute = scrollParent
      .getAttribute(SCROLL_POSITION_ATTRIBUTE)
    const modalContent = scrollParent.classList.contains('modal__background')
      ? targetElement.closest('.modal__content')
      : null
    const previousScrollReserveAttribute = modalContent
      ?.getAttribute(SCROLL_RESERVE_ATTRIBUTE)

    modalContent?.setAttribute(SCROLL_RESERVE_ATTRIBUTE, 'true')
    scrollParent.setAttribute(SCROLL_LOCK_ATTRIBUTE, 'true')
    centerElementInScrollParent(targetElement, scrollParent)
    positionedTargetKeyRef.current = null

    const preventManualScroll = (event) => {
      if (event.cancelable) event.preventDefault()
    }
    const restoreTrainingScrollPosition = () => {
      const lockedScrollTop = Number(
        scrollParent.getAttribute(SCROLL_POSITION_ATTRIBUTE),
      )

      if (
        Number.isFinite(lockedScrollTop)
        && Math.abs(scrollParent.scrollTop - lockedScrollTop) > 0.5
      ) scrollParent.scrollTop = lockedScrollTop
    }

    scrollParent.addEventListener('wheel', preventManualScroll, { passive: false })
    scrollParent.addEventListener('touchmove', preventManualScroll, { passive: false })
    scrollParent.addEventListener('scroll', restoreTrainingScrollPosition)

    return () => {
      scrollParent.removeEventListener('wheel', preventManualScroll)
      scrollParent.removeEventListener('touchmove', preventManualScroll)
      scrollParent.removeEventListener('scroll', restoreTrainingScrollPosition)
      scrollParent.scrollTop = previousScrollTop

      if (previousScrollLockAttribute == null) {
        scrollParent.removeAttribute(SCROLL_LOCK_ATTRIBUTE)
      } else {
        scrollParent.setAttribute(SCROLL_LOCK_ATTRIBUTE, previousScrollLockAttribute)
      }

      if (previousScrollPositionAttribute == null) {
        scrollParent.removeAttribute(SCROLL_POSITION_ATTRIBUTE)
      } else {
        scrollParent.setAttribute(
          SCROLL_POSITION_ATTRIBUTE,
          previousScrollPositionAttribute,
        )
      }

      if (modalContent && previousScrollReserveAttribute == null) {
        modalContent.removeAttribute(SCROLL_RESERVE_ATTRIBUTE)
      } else if (modalContent) {
        modalContent.setAttribute(
          SCROLL_RESERVE_ATTRIBUTE,
          previousScrollReserveAttribute,
        )
      }
    }
  }, [isOpen, spotlightElement, trainingState])

  useEffect(() => {
    if (!isOpen) return undefined

    const previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    let animationFrame
    let targetSearchTimeout
    const updateSpotlight = () => {
      const target = getStepElements(trainingState)
      const targetRect = target?.elements[0]?.getBoundingClientRect()
      const openDropdownsCount = target?.elements.filter(
        (element) => element.matches?.(OPEN_DROPDOWN_SELECTOR),
      ).length ?? 0
      const targetKey = target
        ? [
          trainingState?.step,
          target.phase,
          openDropdownsCount,
          Math.round(targetRect?.top ?? 0),
          Math.round(targetRect?.width ?? 0),
          Math.round(targetRect?.height ?? 0),
          window.innerWidth,
          window.innerHeight,
        ].join(':')
        : null
      const shouldEnsureVisible = targetKey !== positionedTargetKeyRef.current
      const nextSpotlight = getSpotlightState(trainingState, {
        target,
        ensureVisible: shouldEnsureVisible,
      })

      if (nextSpotlight) {
        setSpotlight((currentSpotlight) => (
          isSameSpotlight(currentSpotlight, nextSpotlight)
            ? currentSpotlight
            : nextSpotlight
        ))
        positionedTargetKeyRef.current = targetKey
        setIsTargetMissing(false)
        window.clearTimeout(targetSearchTimeout)
      }

      animationFrame = window.requestAnimationFrame(updateSpotlight)
    }
    updateSpotlight()
    targetSearchTimeout = window.setTimeout(() => {
      if (!getSpotlightState(trainingState, { ensureVisible: false })) {
        setSpotlight(null)
        setIsTargetMissing(true)
      }
    }, TARGET_SEARCH_TIMEOUT)

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeTraining()
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousBodyOverflow
      window.cancelAnimationFrame(animationFrame)
      window.clearTimeout(targetSearchTimeout)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [closeTraining, isOpen, searchAttempt, trainingState])

  useEffect(() => {
    if (!isOpen || !spotlight?.element) return undefined

    const handleTargetClick = (event) => {
      if (
        trainingState.step === PLATFORM_TRAINING_STEPS.SIDEBAR &&
        spotlight.phase === 'item'
      ) {
        event.preventDefault()
        setPlatformTrainingStep(PLATFORM_TRAINING_STEPS.ADD_STUDENT)
        history.push('/users/students')
      } else if (
        trainingState.step === PLATFORM_TRAINING_STEPS.ADD_STUDENT &&
        spotlight.phase === 'point-option'
      ) {
        waitForStepTarget(
          PLATFORM_TRAINING_STEPS.ADD_STUDENT,
          PLATFORM_TRAINING_STEPS.STUDENT_IDENTITY,
          STEP_TARGETS[PLATFORM_TRAINING_STEPS.STUDENT_IDENTITY],
        )
      }
    }

    spotlight.element.addEventListener('click', handleTargetClick)
    return () => spotlight.element?.removeEventListener('click', handleTargetClick)
  }, [history, isOpen, spotlight, trainingState])

  const handleBack = () => {
    if (trainingState.step === PLATFORM_TRAINING_STEPS.ADD_STUDENT) {
      history.push('/')
      setPlatformTrainingStep(PLATFORM_TRAINING_STEPS.SIDEBAR)
      return
    }

    if (trainingState.step === PLATFORM_TRAINING_STEPS.STUDENT_IDENTITY) {
      setPlatformTrainingStep(PLATFORM_TRAINING_STEPS.ADD_STUDENT, {
        identityErrorFields: [],
        identityReturnStep: null,
      })
      return
    }

    if (trainingState.step === PLATFORM_TRAINING_STEPS.STUDENT_SCHEDULE) {
      setPlatformTrainingStep(
        trainingState.paymentMethodStepUsed
          ? PLATFORM_TRAINING_STEPS.STUDENT_PAYMENT_METHOD
          : PLATFORM_TRAINING_STEPS.STUDENT_PAYMENT,
      )
      return
    }

    if (trainingState.step === PLATFORM_TRAINING_STEPS.STUDENT_SAVE) {
      setPlatformTrainingStep(
        trainingState.usesPhone || trainingState.usesLogin
          ? PLATFORM_TRAINING_STEPS.STUDENT_PASSWORD
          : PLATFORM_TRAINING_STEPS.STUDENT_SCHEDULE,
      )
      return
    }

    const previousStep = PREVIOUS_STEPS[trainingState.step]
    if (previousStep) setPlatformTrainingStep(previousStep)
  }

  const handleNext = () => {
    if (trainingState.step === PLATFORM_TRAINING_STEPS.STUDENT_IDENTITY) {
      const name = getTargetInputValue(spotlight?.element, 'name')
      const email = getTargetInputValue(spotlight?.element, 'email')
      const phone = getTargetInputValue(spotlight?.element, 'phone')
      const login = getTargetInputValue(spotlight?.element, 'login')
      const usesEmail = email.length > 0
      const usesPhone = phone.replace(/\D/g, '').length >= 10
      const usesLogin = login.length > 0

      if (name && (email || usesPhone || usesLogin)) {
        setPlatformTrainingStep(
          trainingState.identityReturnStep
            ?? PLATFORM_TRAINING_STEPS.STUDENT_COURSE,
          {
            identityErrorFields: [],
            identityReturnStep: null,
            usesEmail,
            usesPhone,
            usesLogin,
          },
        )
      }
      return
    }

    if (trainingState.step === PLATFORM_TRAINING_STEPS.STUDENT_COURSE) {
      const selectedOptionsCount = spotlight?.element
        ?.querySelectorAll('.ant-select-selection-item')?.length ?? 0

      if (selectedOptionsCount >= 2 && getOpenDropdowns().length === 0) {
        setPlatformTrainingStep(PLATFORM_TRAINING_STEPS.STUDENT_TARIFF)
      }
      return
    }

    if (trainingState.step === PLATFORM_TRAINING_STEPS.STUDENT_PAYMENT) {
      const paymentValue = getTargetInputValue(spotlight?.element, 'payment_amount')
        .replace(/\s/g, '')
        .replace(',', '.')
      const hasPayment = Number(paymentValue) > 0
      const paymentMethodTarget = getVisibleElements(
        `[data-platform-training-target="${STEP_TARGETS[PLATFORM_TRAINING_STEPS.STUDENT_PAYMENT_METHOD]}"]`,
      )[0]
      const paymentMethodStepUsed = hasPayment && !!paymentMethodTarget

      setPlatformTrainingStep(
        paymentMethodStepUsed
          ? PLATFORM_TRAINING_STEPS.STUDENT_PAYMENT_METHOD
          : PLATFORM_TRAINING_STEPS.STUDENT_SCHEDULE,
        { paymentMethodStepUsed },
      )
      return
    }

    if (trainingState.step === PLATFORM_TRAINING_STEPS.STUDENT_PAYMENT_METHOD) {
      if (hasSelectedOption(spotlight?.element)) {
        setPlatformTrainingStep(PLATFORM_TRAINING_STEPS.STUDENT_SCHEDULE)
      }
      return
    }

    if (trainingState.step === PLATFORM_TRAINING_STEPS.STUDENT_PASSWORD) {
      if (getTargetInputValue(spotlight?.element, 'password')) {
        setPlatformTrainingStep(PLATFORM_TRAINING_STEPS.STUDENT_SAVE)
      }
      return
    }

    const nextStep = NEXT_STEPS[trainingState.step]

    if (nextStep) {
      setPlatformTrainingStep(nextStep)
      return
    }

    if (trainingState.step === PLATFORM_TRAINING_STEPS.STUDENT_SCHEDULE) {
      setPlatformTrainingStep(
        trainingState.usesPhone
          || trainingState.usesLogin
          ? PLATFORM_TRAINING_STEPS.STUDENT_PASSWORD
          : PLATFORM_TRAINING_STEPS.STUDENT_SAVE,
      )
      return
    }

    if (trainingState.step === PLATFORM_TRAINING_STEPS.CREATED_STUDENT) {
      closeTraining()
    }
  }

  if (!isOpen) return null

  if (!spotlight) {
    return (
      <div className={s.overlay} role="dialog" aria-label="Обучение по платформе">
        <div className={s.fullShade} />
        <div className={s.loadingTooltip}>
          <button
            type="button"
            className={s.closeButton}
            onClick={closeTraining}
            aria-label="Закрыть обучение"
          >
            <CloseOutlined />
          </button>
          {isTargetMissing ? (
            <>
              <span className={s.missingTitle}>Не удалось найти нужный элемент</span>
              <span className={s.missingText}>
                Проверьте доступность раздела и повторите поиск.
              </span>
              <button
                type="button"
                className={s.retryButton}
                onClick={() => {
                  setIsTargetMissing(false)
                  setSearchAttempt((attempt) => attempt + 1)
                }}
              >
                Повторить
              </button>
            </>
          ) : 'Подготавливаем следующий шаг…'}
        </div>
      </div>
    )
  }

  const { rect, phase } = spotlight
  const width = rect.right - rect.left
  const height = rect.bottom - rect.top
  const description = trainingState.step === PLATFORM_TRAINING_STEPS.SIDEBAR
    ? phase === 'folder'
      ? 'Раскройте папку, чтобы перейти в раздел «Ученики и коллеги».'
      : 'Перейдите в данный раздел, чтобы создать первого ученика.'
    : trainingState.step === PLATFORM_TRAINING_STEPS.STUDENT_IDENTITY
      && trainingState.identityErrorFields?.length
      ? getIdentityErrorDescription(trainingState.identityErrorFields)
      : trainingState.step === PLATFORM_TRAINING_STEPS.STUDENT_PASSWORD
        && trainingState.usesEmail
        ? 'Вы указали e-mail учащегося, а также номер телефона или логин. Создайте для учащегося пароль вручную и передайте данные для входа.'
        : STEP_DESCRIPTIONS[trainingState.step]
  const currentTooltipSize = tooltipSize?.step === trainingState.step
    ? tooltipSize
    : null
  const tooltipPosition = getTooltipPosition(rect, currentTooltipSize)
  const tooltipAvailableHeight = window.innerWidth <= 720
    ? window.innerHeight - 32
    : tooltipPosition.bottom != null
      ? window.innerHeight - tooltipPosition.bottom - 16
      : window.innerHeight - tooltipPosition.top - 16
  const identityReady = trainingState.step === PLATFORM_TRAINING_STEPS.STUDENT_IDENTITY
    && !!getTargetInputValue(spotlight.element, 'name')
    && (
      !!getTargetInputValue(spotlight.element, 'email')
      || getTargetInputValue(spotlight.element, 'phone').replace(/\D/g, '').length >= 10
      || !!getTargetInputValue(spotlight.element, 'login')
    )
  const passwordReady = trainingState.step === PLATFORM_TRAINING_STEPS.STUDENT_PASSWORD
    && !!getTargetInputValue(spotlight.element, 'password')
  const isPasswordStep = trainingState.step === PLATFORM_TRAINING_STEPS.STUDENT_PASSWORD
  const paymentMethodReady = trainingState.step === PLATFORM_TRAINING_STEPS.STUDENT_PAYMENT_METHOD
    && hasSelectedOption(spotlight.element)
  const courseReady = trainingState.step === PLATFORM_TRAINING_STEPS.STUDENT_COURSE
    && spotlight.element.querySelectorAll('.ant-select-selection-item').length >= 2
    && getOpenDropdowns().length === 0
  const showBackButton = trainingState.step === PLATFORM_TRAINING_STEPS.ADD_STUDENT
    || trainingState.step === PLATFORM_TRAINING_STEPS.STUDENT_IDENTITY
    || trainingState.step === PLATFORM_TRAINING_STEPS.STUDENT_SCHEDULE
    || trainingState.step === PLATFORM_TRAINING_STEPS.STUDENT_SAVE
    || !!PREVIOUS_STEPS[trainingState.step]
  const showNextButton = identityReady
    || courseReady
    || passwordReady
    || isPasswordStep
    || paymentMethodReady
    || trainingState.step === PLATFORM_TRAINING_STEPS.STUDENT_PAYMENT
    || !!NEXT_STEPS[trainingState.step]
    || trainingState.step === PLATFORM_TRAINING_STEPS.STUDENT_SCHEDULE
    || trainingState.step === PLATFORM_TRAINING_STEPS.CREATED_STUDENT

  return (
    <div className={s.overlay} role="dialog" aria-label="Обучение по платформе">
      <div
        className={s.shade}
        style={{ top: 0, left: 0, right: 0, height: rect.top }}
      />
      <div
        className={s.shade}
        style={{ top: rect.top, left: 0, width: rect.left, height }}
      />
      <div
        className={s.shade}
        style={{ top: rect.top, left: rect.right, right: 0, height }}
      />
      <div
        className={s.shade}
        style={{ top: rect.bottom, left: 0, right: 0, bottom: 0 }}
      />
      <div
        className={s.spotlightMask}
        style={{ top: rect.top, left: rect.left, width, height }}
      />

      <div
        ref={tooltipRef}
        className={s.tooltip}
        style={{
          ...tooltipPosition,
          maxHeight: Math.min(360, Math.max(0, tooltipAvailableHeight)),
        }}
      >
        <button
          type="button"
          className={s.closeButton}
          onClick={closeTraining}
          aria-label="Закрыть обучение"
        >
          <CloseOutlined />
        </button>
        <span className={s.step}>Обучение по платформе</span>
        <p>{description}</p>
        {(showBackButton || showNextButton) && (
          <div className={s.actions}>
            {showBackButton && (
              <button
                type="button"
                className={s.backButton}
                onClick={handleBack}
              >
                Назад
              </button>
            )}
            {showNextButton && (
              <button
                type="button"
                className={s.nextButton}
                onClick={handleNext}
                disabled={isPasswordStep && !passwordReady}
              >
                {trainingState.step === PLATFORM_TRAINING_STEPS.CREATED_STUDENT
                  ? 'Завершить'
                  : 'Далее'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default PlatformTrainingOverlay
